import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';

// Import các Custom Hooks phục vụ gọi API dữ liệu
import { useCandidateProfiles } from '@/hooks/useCandidate';
import { useMyJobs, useDeleteJobPosting } from '@/hooks/useJob';
import { useRecruiterOffers, useRespondOffer, useUpdateOfferStage } from '@/hooks/useOffer';
import { useCreateConversation } from '@/hooks/useChat';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';

// Import các sub-pages & components dùng chung
import SearchCandidatesPage from '../SearchCandidatesPage';
import SavedCandidatesPage from '../SavedCandidatesPage';
import CreateJobPage from '@/pages/jobs/CreateJobPage';
import CompanyProfileSettings from '@/components/company/CompanyProfileSettings';
import { CandidateDetailModal } from '@/components/jobs/CandidateDetailModal';
import { RecruiterNoteModal } from '@/components/jobs/RecruiterNoteModal';
import { CandidateFilter } from '@/types/candidates/candidates.types';
import { formatSalaryRange } from '@/lib/utils';
import dayjs from '@/lib/dayjs';

// Import các Sub-Components đã được chia nhỏ theo mô-đun
import { RecruiterSidebar } from './RecruiterSidebar';
import { RecruiterHeader } from './RecruiterHeader';
import { RecruiterOverviewTab } from './RecruiterOverviewTab';
import { RecruiterPostingsTab } from './RecruiterPostingsTab';
import { RecruiterInterviewsTab } from './RecruiterInterviewsTab';
import { RecruiterEvaluationsTab } from './RecruiterEvaluationsTab';
import { RecruiterNotificationsTab } from './RecruiterNotificationsTab';
import { RecruiterReportsTab } from './RecruiterReportsTab';
import { RecruiterActionMenu } from './RecruiterActionMenu';

/**
 * Trang Quản Lý Tuyển Dụng Tổng Thể (Recruiter Dashboard)
 * Điều phối dữ liệu state chính, gọi các API hooks và tích hợp các sub-components giao diện.
 */
export default function RecruiterManageJobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();

  // 1. Quản lý trạng thái đóng/mở Sidebar & Tab hoạt động
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isFindCandidatesExpanded, setIsFindCandidatesExpanded] = useState(true);
  const [isManagePostingsExpanded, setIsManagePostingsExpanded] = useState(true);

  // Tab hiện tại được xác định qua URL Path
  const getActiveTabFromPath = (pathname: string, searchTab?: string | null) => {
    if (pathname.includes('/thong-tin-cong-ty')) return 'settings';
    if (pathname.includes('/tin-dang')) return 'postings';
    if (pathname.includes('/tim-ung-vien')) return 'candidates';
    if (pathname.includes('/ung-vien-da-luu')) return 'saved_candidates';
    if (pathname.includes('/dang-tin')) return 'post_job';
    if (pathname.includes('/lich-phong-van')) return 'interviews';
    if (pathname.includes('/danh-gia')) return 'evaluations';
    if (pathname.includes('/thong-bao')) return 'notifications';
    if (pathname.includes('/bao-cao')) return 'reports';
    if (pathname.includes('/ung-vien')) return 'overview';
    if (searchTab) return searchTab;
    return 'overview';
  };

  const activeTab = getActiveTabFromPath(location.pathname, searchParams.get('tab'));

  const setActiveTab = (tab: string) => {
    const pathToTab: Record<string, string> = {
      overview: '/viec-lam/quan-ly/ung-vien',
      postings: '/viec-lam/quan-ly/tin-dang',
      candidates: '/viec-lam/quan-ly/tim-ung-vien',
      saved_candidates: '/viec-lam/quan-ly/ung-vien-da-luu',
      settings: '/viec-lam/quan-ly/thong-tin-cong-ty',
      post_job: '/viec-lam/quan-ly/dang-tin',
      interviews: '/viec-lam/quan-ly/lich-phong-van',
      evaluations: '/viec-lam/quan-ly/danh-gia',
      notifications: '/viec-lam/quan-ly/thong-bao',
      reports: '/viec-lam/quan-ly/bao-cao',
    };
    navigate(pathToTab[tab] || '/viec-lam/quan-ly/ung-vien');
  };

  // 2. Trạng thái Bộ Lọc Ứng Viên & Tìm Kiếm Tin Đăng
  const [candidateFilters, setCandidateFilters] = useState<Partial<CandidateFilter>>({});
  const [postingSearchQuery, setPostingSearchQuery] = useState('');

  // 3. Trạng thái Anchor El cho MUI Action Menu (3 chấm)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCandidateId, setMenuCandidateId] = useState<string | null>(null);

  // 4. Trạng thái Modals (Xem chi tiết hồ sơ & Ghi chú)
  const [selectedDetailCandidate, setSelectedDetailCandidate] = useState<any | null>(null);
  const [noteCandidate, setNoteCandidate] = useState<any | null>(null);

  // 5. Trạng thái Phân Trang
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // --- GỌI DỮ LIỆU TỪ BACKEND API HOOKS ---
  // Lấy danh sách tin tuyển dụng của Nhà tuyển dụng
  const { data: myJobsData, isLoading: loadingJobs, refetch: refetchJobs } = useMyJobs();
  const myJobs = Array.isArray(myJobsData) ? myJobsData : (myJobsData as any)?.data || [];

  // Lấy toàn bộ danh sách ứng viên nộp hồ sơ để tính số liệu thống kê chính xác
  const { data: allOffersData } = useRecruiterOffers(user?.id || '', { limit: 100 });
  const allOffers = Array.isArray(allOffersData) ? allOffersData : (allOffersData as any)?.data || [];

  // Lấy danh sách ứng viên đã nộp hồ sơ (Recruiter Offers) theo phân trang
  const { data: offersData, isLoading: loadingOffers, refetch: refetchOffers } = useRecruiterOffers(user?.id || '', {
    page: currentPage,
    limit: pageSize,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });
  const offers = Array.isArray(offersData) ? offersData : (offersData as any)?.data || [];
  const totalOffersCount = (offersData as any)?.total || (allOffersData as any)?.total || offers.length;

  const sourceOffers = allOffers.length > 0 ? allOffers : offers;

  // Lấy tổng số ứng viên tìm kiếm (API Candidate Profiles)
  const { data: candidatesRes, isLoading: loadingCandidatesProfiles } = useCandidateProfiles({
    page: 1,
    limit: 10,
    ...candidateFilters,
  });
  const totalApiCandidates = candidatesRes?.total || 0;

  // --- THỰC THI THAO TÁC MUTATIONS ---
  const { mutate: deleteJob } = useDeleteJobPosting();
  const { mutate: updateOfferStage } = useUpdateOfferStage();
  const { mutate: createConversation } = useCreateConversation();

  // Tính toán số liệu thống kê cho các thẻ Stat Cards liên quan đến Ứng viên
  const inProgressOffersCount = sourceOffers.filter((o: any) => {
    const st = String(o.status || '').toLowerCase();
    const sg = String(o.stage || '').toUpperCase();
    return sg !== 'HIRED' && sg !== 'REJECTED' && st !== 'rejected';
  }).length;

  const hiredOffersCount = sourceOffers.filter((o: any) => {
    const st = String(o.status || '').toLowerCase();
    const sg = String(o.stage || '').toUpperCase();
    return sg === 'HIRED' || st === 'accepted';
  }).length;

  const rejectedOffersCount = sourceOffers.filter((o: any) => {
    const st = String(o.status || '').toLowerCase();
    const sg = String(o.stage || '').toUpperCase();
    return sg === 'REJECTED' || st === 'rejected';
  }).length;

  // Chuẩn hóa danh sách ứng viên cho bảng CandidateTable
  const realCandidates = offers.map((offer: any) => {
    const associatedJob = myJobs.find((j: any) => j.id === offer.entityId);
    const jobTitle = associatedJob?.title || offer.entityData?.title || 'Vị trí F&B';
    const c = offer.candidateProfile || {};

    const rawStatus = String(offer.status || offer.approvalStatus || '').toLowerCase();
    let status = 'pending';
    let statusLabel = 'Đang chờ duyệt';
    if (rawStatus === 'approved') {
      status = 'approved';
      statusLabel = 'Đã duyệt';
    } else if (rawStatus === 'rejected') {
      status = 'rejected';
      statusLabel = 'Từ chối';
    }

    return {
      id: offer.id,
      isReal: true,
      fullName: c.fullName || offer.content?.candidateName || `Ứng viên #${offer.senderId?.substring(0, 5) || 'F&B'}`,
      email: c.email || offer.content?.candidateEmail || 'Chưa cập nhật',
      phoneNumber: c.phoneNumber || '',
      position: c.desiredPosition || associatedJob?.jobCategoryName || 'Nhân viên F&B',
      categoryAndType: `${c.jobCategoryName || ''} ${c.jobCategoryName && c.jobType ? '•' : ''} ${c.jobType || ''}`.trim() || 'Chưa cập nhật',
      experience: c.experience ? `${c.experience} năm` : 'Chưa cập nhật KN',
      education: c.education || 'Chưa cập nhật HV',
      salary: formatSalaryRange(c.expectedSalaryMin, c.expectedSalaryMax),
      location: c.province?.name || c.location || associatedJob?.workingLocation || 'Toàn quốc',
      stage: offer.stage || 'NEW',
      status,
      statusLabel,
      date: dayjs(offer.createdAt).format('DD/MM/YYYY'),
      avatar: c.images?.find((img: any) => img.isPrimary)?.image?.publicUrl || "",
      recruiterNote: offer.recruiterNote?.notes || '',
      message: offer.message || 'Không có thư giới thiệu.',
      startDate: offer.content?.startDate,
      rawProfile: c,
      rawOffer: offer,
      jobTitle,
    };
  });

  // Lọc danh sách bài đăng tuyển dụng theo từ khóa
  const filteredJobs = myJobs.filter((job: any) =>
    (job.title || '').toLowerCase().includes(postingSearchQuery.toLowerCase()) ||
    (job.jobCategoryName || '').toLowerCase().includes(postingSearchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(totalOffersCount / pageSize) || 1;

  // --- CÁC HÀM XỬ LÝ HÀNH ĐỘNG (HANDLERS) ---
  // Xóa bài đăng tuyển dụng
  const handleDeleteJob = (id: string) => {
    if (confirm('Bạn có chắc chắn muốn xóa tin tuyển dụng này?')) {
      deleteJob(id, {
        onSuccess: () => refetchJobs(),
      });
    }
  };

  // Duyệt/Từ chối hồ sơ ứng tuyển từ modal
  const handleOfferResponse = (id: string, status: 'accepted' | 'rejected') => {
    const stage = status === 'accepted' ? 'CONTACTED' : 'REJECTED';
    updateOfferStage(
      { id, stage },
      {
        onSuccess: () => {
          refetchOffers();
          setAnchorEl(null);
          setMenuCandidateId(null);
          if (selectedDetailCandidate && selectedDetailCandidate.id === id) {
            setSelectedDetailCandidate(null);
          }
        },
      }
    );
  };

  // Thay đổi trạng thái quy trình tuyển dụng (Liên hệ, Phỏng vấn, Thử việc, Tuyển dụng, Từ chối)
  const handleOfferStageChange = (id: string, stage: 'CONTACTED' | 'INTERVIEW' | 'PROBATION' | 'HIRED' | 'REJECTED') => {
    updateOfferStage(
      { id, stage },
      {
        onSuccess: () => {
          refetchOffers();
          setAnchorEl(null);
          setMenuCandidateId(null);
          if (selectedDetailCandidate && selectedDetailCandidate.id === id) {
            setSelectedDetailCandidate(null);
          }
        },
      }
    );
  };

  // Bật menu thao tác 3 chấm
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, candidateId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuCandidateId(candidateId);
  };

  // Đóng menu thao tác 3 chấm
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuCandidateId(null);
  };

  const currentMenuCandidate = realCandidates.find((c: any) => c.id === menuCandidateId);

  // Tạo cuộc trò chuyện nhắn tin với ứng viên
  const handleStartChat = () => {
    if (currentMenuCandidate && currentMenuCandidate.isReal) {
      const participantId = currentMenuCandidate.rawOffer?.senderId;
      if (participantId) {
        const chatTitle = `Trao đổi ứng tuyển: ${currentMenuCandidate.fullName} - ${currentMenuCandidate.jobTitle || currentMenuCandidate.position}`;
        createConversation(
          { participantId, title: chatTitle },
          {
            onSuccess: () => {
              handleCloseMenu();
              navigate('/tin-nhan');
            },
            onError: () => {
              addToast('error', 'Có lỗi xảy ra khi tạo cuộc trò chuyện');
            },
          }
        );
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#fafafc', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative', fontFamily: 'sans-serif', color: '#1e293b' }}>

      {/* 1. THANH MENU SIDEBAR BÊN TRÁI */}
      <RecruiterSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        isFindCandidatesExpanded={isFindCandidatesExpanded}
        setIsFindCandidatesExpanded={setIsFindCandidatesExpanded}
        isManagePostingsExpanded={isManagePostingsExpanded}
        setIsManagePostingsExpanded={setIsManagePostingsExpanded}
      />

      {/* 2. KHỐI NỘI DUNG CHÍNH (TOP BAR + CÁC TAB HIỂN THỊ DỘNG) */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>

        {/* Header Trên Cùng Di Động */}
        <RecruiterHeader
          user={user}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        />

        {/* Khung Nội Dung Theo Tab Đang Chọn */}
        <Box component="main" sx={{ p: { xs: 3, md: 4 }, flex: 1, overflowY: 'auto', bgcolor: '#fafafc' }}>

          {/* TAB OVERVIEW: QUẢN LÝ ỨNG VIÊN */}
          {activeTab === 'overview' && (
            <RecruiterOverviewTab
              inProgressOffersCount={inProgressOffersCount}
              hiredOffersCount={hiredOffersCount}
              rejectedOffersCount={rejectedOffersCount}
              candidateFilters={candidateFilters}
              setCandidateFilters={setCandidateFilters}
              setCurrentPage={setCurrentPage}
              loadingOffers={loadingOffers}
              loadingCandidatesProfiles={loadingCandidatesProfiles}
              paginatedCandidates={realCandidates}
              setSelectedDetailCandidate={setSelectedDetailCandidate}
              handleOpenMenu={handleOpenMenu}
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              totalPages={totalPages}
              setActiveTab={setActiveTab}
            />
          )}

          {/* TAB POSTINGS: QUẢN LÝ TIN ĐĂNG */}
          {activeTab === 'postings' && (
            <RecruiterPostingsTab
              myJobsCount={myJobs.length}
              postingSearchQuery={postingSearchQuery}
              setPostingSearchQuery={setPostingSearchQuery}
              setActiveTab={setActiveTab}
              loadingJobs={loadingJobs}
              filteredJobs={filteredJobs}
              handleDeleteJob={handleDeleteJob}
            />
          )}

          {/* TAB CANDIDATES: TÌM KIẾM ỨNG VIÊN MỚI */}
          {activeTab === 'candidates' && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="border-b pb-5">
                <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Tìm kiếm ứng viên</h1>
              </div>
              <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
                <SearchCandidatesPage />
              </div>
            </div>
          )}

          {/* TAB SAVED CANDIDATES: ỨNG VIÊN ĐÃ LƯU */}
          {activeTab === 'saved_candidates' && (
            <div className="space-y-6 animate-fade-in text-left">
              <div className="border-b pb-5">
                <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Ứng viên đã lưu</h1>
              </div>
              <SavedCandidatesPage />
            </div>
          )}

          {/* TAB INTERVIEWS: LỊCH PHỎNG VẤN */}
          {activeTab === 'interviews' && <RecruiterInterviewsTab />}

          {/* TAB EVALUATIONS: ĐÁNH GIÁ ỨNG VIÊN */}
          {activeTab === 'evaluations' && <RecruiterEvaluationsTab />}

          {/* TAB NOTIFICATIONS: THÔNG BÁO */}
          {activeTab === 'notifications' && <RecruiterNotificationsTab />}

          {/* TAB REPORTS: BÁO CÁO TUYỂN DỤNG */}
          {activeTab === 'reports' && <RecruiterReportsTab />}

          {/* TAB SETTINGS: THÔNG TIN CÔNG TY */}
          {activeTab === 'settings' && <CompanyProfileSettings />}

          {/* TAB POST_JOB: ĐĂNG TIN MỚI */}
          {activeTab === 'post_job' && (
            <CreateJobPage
              onSuccess={() => {
                refetchJobs();
                setActiveTab('postings');
              }}
              onCancel={() => setActiveTab('postings')}
            />
          )}
        </Box>
      </Box>

      {/* 3. MODAL XEM CHI TIẾT HỒ SƠ ỨNG VIÊN */}
      <CandidateDetailModal
        open={Boolean(selectedDetailCandidate)}
        candidate={selectedDetailCandidate}
        onClose={() => setSelectedDetailCandidate(null)}
        onAccept={(id) => handleOfferResponse(id, 'accepted')}
        onReject={(id) => handleOfferResponse(id, 'rejected')}
      />

      {/* 4. MENU THAO TÁC 3 CHẤM BẢNG ỨNG VIÊN */}
      <RecruiterActionMenu
        anchorEl={anchorEl}
        handleCloseMenu={handleCloseMenu}
        currentMenuCandidate={currentMenuCandidate}
        handleOfferStageChange={handleOfferStageChange}
        handleStartChat={handleStartChat}
        setSelectedDetailCandidate={setSelectedDetailCandidate}
        setNoteCandidate={setNoteCandidate}
      />

      {/* 5. MODAL GHI CHÚ NHÀ TUYỂN DỤNG */}
      {noteCandidate && (
        <RecruiterNoteModal
          open={!!noteCandidate}
          onClose={() => setNoteCandidate(null)}
          candidateProfileId={noteCandidate.rawProfile?.id}
          candidateName={noteCandidate.fullName}
          existingNote={noteCandidate.rawOffer?.recruiterNote || noteCandidate.rawProfile?.recruiterNote}
        />
      )}
    </Box>
  );
}
